import {getAmbientDisplayClassNumber} from "../Ambients/ambientClassPresentation";

export const clsPresc = {
  0: "mbp",
  1: "bp",
  2: "pp",
  3: "ap",
  4: "map",
  5: "five",
  6: "six",
};

export const sortOrder = [
  "six",
  "five",
  "map",
  "ap",
  "pp",
  "bp",
  "mbp",
];

export const PRODUCT_FIELDS = [
  {
    key: "prod1",
    column: 0,
    defaultUnit: "10",
    labelKey: "prescription:creation.form.product",
    labelIndex: 1,
    units: ["10", "20", "30", "40", "50"],
  },
  {
    key: "prod2",
    column: 1,
    defaultUnit: "40",
    labelKey: "prescription:creation.form.product",
    labelIndex: 2,
    units: ["40", "50"],
  },
  {
    key: "prod3",
    column: 2,
    defaultUnit: "50",
    labelKey: "prescription:creation.form.product",
    labelIndex: 3,
    units: ["50", "40"],
  },
];

export const getColor = (key, numClasses) => {
  return {
    2: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(26,150,65)",
    },
    3: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(255,255,192)",
      pp: "rgb(26,150,65)",
    },
    4: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(254,201,129)",
      pp: "rgb(196,230,135)",
      ap: "rgb(26,150,65)",
    },
    5: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(253,174,97)",
      pp: "rgb(255,255,192)",
      ap: "rgb(166,217,106)",
      map: "rgb(26,150,65)",
    },
    6: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(246,144,83)",
      pp: "rgb(255,223,154)",
      ap: "rgb(220,240,158)",
      map: "rgb(138,204,98)",
      five: "rgb(26,150,65)",
    },
    7: {
      mbp: "rgb(215,25,28)",
      bp: "rgb(241,124,74)",
      pp: "rgb(254,201,129)",
      ap: "rgb(255,255,192)",
      map: "rgb(196,230,135)",
      five: "rgb(119,195,92)",
      six: "rgb(26,150,65)",
    },
  }[numClasses][key];
};

export const getUnitLabel = (unitValue, t) => {
  const labels = {
    10: t("prescription:creation.units.seed_ha"),
    20: t("prescription:creation.units.seed_m2"),
    30: t("prescription:creation.units.seed_m"),
    40: t("prescription:creation.units.kg_ha"),
    50: t("prescription:creation.units.lt_ha"),
  };

  return labels[unitValue];
};

export const getTotalUnitLabel = (unitValue, t) => {
  const labels = {
    10: t("prescription:creation.units.seed_total"),
    20: t("prescription:creation.units.seed_total"),
    30: t("prescription:creation.units.seed_total"),
    40: t("prescription:creation.units.kg"),
    50: t("prescription:creation.units.lt"),
  };

  return labels[unitValue];
};

export const getProductTotal = (value, hectares, unitValue, rowSpacingCm) => {
  if (!Number.isFinite(value) || !Number.isFinite(hectares)) {
    return null;
  }

  if (unitValue === "20") {
    return value * hectares * 10000;
  }

  if (unitValue === "30") {
    if (!Number.isFinite(rowSpacingCm) || rowSpacingCm <= 0) {
      return null;
    }

    return value * hectares * 10000 / (rowSpacingCm / 100);
  }

  return value * hectares;
};

export const getNumericInputValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(String(value).replace(",", "."));

  return Number.isFinite(numericValue) ? numericValue : null;
};

export const getDisplayClassNumber = getAmbientDisplayClassNumber;

export const buildPrescriptionModel = ({
  columns,
  numClasses,
  prescription,
  prod,
  rowSpacingCm,
  t,
  units,
}) => {
  const rowSpacingValue = getNumericInputValue(rowSpacingCm);
  const totalHa = Object.keys(prescription).reduce((acc, key) => acc + (Number(prescription[key].ha) || 0), 0);
  const filtered = sortOrder.filter((key) => prescription[key].ha > 0);
  const needsRowSpacing = PRODUCT_FIELDS.some((field) => units[field.key] === "30");
  const getProductLabel = (field) => t(field.labelKey, {index: field.labelIndex});

  const classRows = filtered.map((key) => {
    const cls = prescription[key].cls;
    const ha = Number(prescription[key].ha) || 0;
    const percent = totalHa > 0 ? (ha * 100) / totalHa : 0;

    return {
      key,
      cls,
      ha,
      percent,
      color: getColor(key, numClasses),
      label: `${t("prescription:creation.form.class")} ${getDisplayClassNumber(cls, numClasses)}`,
    };
  });

  const productSummaries = PRODUCT_FIELDS.map((field) => {
    const rows = classRows.map((row) => {
      const value = getNumericInputValue(columns[field.column][row.cls]);

      return {
        ...row,
        value,
        total: value === null ? null : getProductTotal(value, row.ha, units[field.key], rowSpacingValue),
      };
    });

    const isComplete = rows.length > 0 && rows.every((row) => row.value !== null);

    if (!isComplete) {
      return null;
    }

    const totalAmount = rows.every((row) => row.total !== null)
      ? rows.reduce((acc, row) => acc + row.total, 0)
      : null;
    const weightedAverage = totalHa > 0
      ? rows.reduce((acc, row) => acc + (row.value * row.ha), 0) / totalHa
      : 0;

    return {
      key: field.key,
      name: prod[field.key] || getProductLabel(field),
      rows,
      totalAmount,
      totalHa,
      totalUnitLabel: getTotalUnitLabel(units[field.key], t),
      unitLabel: getUnitLabel(units[field.key], t),
      weightedAverage,
    };
  }).filter(Boolean);

  return {
    classRows,
    filtered,
    getProductLabel,
    needsRowSpacing,
    productSummaries,
    rowSpacingValue,
    totalHa,
  };
};
